/**
 * IQ-Service-Inventory Datenmodell (Spec 344 — per-Merchant CRUD + Wizard-Dropdown).
 *
 * camelCase-Spiegel des BFF-Contracts (Contract v1.0, authoritative,
 * openapi-admin-iq-services-v1.0.yaml). Der BFF mappt PascalCase<->camelCase an der
 * Naht; das Frontend sieht nur camelCase.
 *
 * Contract v1.0: gegen Draft v0.9 gebaut und ueber die Live-Verifikation (#346) auf den
 * As-built-Stand nachgezogen — der finale v1.0 deckt sich mit diesen Shapes; der
 * Final-Contract-Sync war ein No-Op.
 *
 * ⚠ NICHT verwechseln mit dem platform-`iq-services` (Gateway-Config, src/types/
 * iq-service.ts mit IqServiceStatus = 'active'|'inactive'|'suspended'). Andere Entitaet,
 * andere Enum-Werte.
 */

/**
 * Opaque IQ-service key validation (= gatewayServiceId values). Deliberately liberal:
 * only structural breakers (whitespace, # / ? % &) are excluded; max 128. Mirrors the
 * Facade contract so the UI gives early feedback. SINGLE source — used by the register
 * form (Phase 5) and the relaxed FallbackChain/Service-form validation (Phase 6, UC-5).
 * Lives in the neutral types layer so the services feature can import it without
 * depending on the iq-service-inventory component module.
 */
export const IQ_SERVICE_KEY_PATTERN = /^[^\s#/?%&]{1,128}$/;

/** Inventar-/Sichtbarkeits-Status (KEINE Rolle im TX-Control-Flow). */
export type IqServiceInventoryStatus = 'active' | 'pending_provisioning' | 'archived';

/** Operator-Annotation: fachlicher Flow (nicht aus dem Key geparst). */
export type IqServiceFlowType = 'WAP_CLICK' | 'WEB_TAN';

/** Operator-Annotation: Billing-Type (nicht aus dem Key geparst). */
export type IqServiceBookingType = 'SUBSCRIPTION' | 'OTP';

/**
 * Verweis von einem FallbackChain-Eintrag zurueck auf den Service, der ihn haelt
 * (Spec 347). BFF-abgeleitet — Spiegel des Backend-`RoutingRef`. Dient dem
 * Tooltip / Drill-down der Nutzungs-Anzeige.
 */
export interface RoutingRef {
  serviceId: string;
  displayName: string;
  merchantId: string;
  flowType?: string;
}

/** Ein IQ-Service-Inventar-Eintrag eines Merchants. */
export interface IqServiceInventoryItem {
  iqServiceKey: string;
  merchantId: string;
  flowType?: IqServiceFlowType;
  bookingType?: IqServiceBookingType;
  displayName?: string;
  description?: string;
  internetqEmailRef?: string;
  status: IqServiceInventoryStatus;
  registeredAt: string;
  registeredBy: string;
  /** Optimistic-Lock-Token — beim PUT mitschicken. */
  updatedAt: string;
  updatedBy?: string;
  // --- Spec 347: BFF-abgeleitete Nutzung (Dimension a), additiv/optional ---
  /** Anzahl FallbackChain-Eintraege, die diese Kennung referenzieren (Badge-Wert,
   *  Eintrags-Zaehlung — NICHT `usedBy.length`). */
  fallbackUsageCount?: number;
  /** Distinkte referenzierende Services (Tooltip / Drill-down). */
  usedBy?: RoutingRef[];
  /** Service-Liste nicht abrufbar → Nutzung nicht ermittelbar. */
  usageUnavailable?: boolean;
  /** Seiten-Schranke erreicht → Nutzung ggf. unvollstaendig. */
  usageTruncated?: boolean;
}

/**
 * Eine Zeile der plattformweiten Cross-Merchant-Uebersicht (Spec 347 §8).
 * Spiegel des BFF-`OverviewRow` (iq-service-inventory-overview-handler).
 * Nur die Gateway-Nutzungs-Dimension (a) — die rohe Aggregat-Antwort traegt
 * keine Credential-Dimension pro Zeile.
 */
export interface PlatformIqInventoryRow {
  merchantId: string;
  iqServiceKey: string;
  status: IqServiceInventoryStatus;
  /** Eintrags-Zaehlung (Dimension a), merchant-gescopt. */
  fallbackUsageCount: number;
  /** Distinkte referenzierende Services (Drill-down), merchant-gescopt. */
  usedBy: RoutingRef[];
  // --- Spec 384: BFF-durchgereichte Operator-Annotationen (additiv/optional) ---
  // Eigene Annotationen des Eintrags (NICHT aus dem #347-Service-Join abgeleitet). Die
  // Enum-Felder lassen bewusst auch einen rohen String zu: ein Facade-seitig neuer
  // Enum-Wert wird in der UI verbatim gezeigt statt versteckt (Unknown-Enum-Toleranz).
  /** Anzeigename; leer → UI-Fallback „—". */
  displayName?: string;
  /** Fachlicher Flow; bekannter Enum → Label, sonst Rohwert; leer → „—". */
  flowType?: IqServiceFlowType | string;
  /** Billing-Type; bekannter Enum → Label, sonst Rohwert; leer → „—". */
  bookingType?: IqServiceBookingType | string;
}

/**
 * Spec 384 §2: client-seitige Filter der Plattform-Übersicht. Der Aggregat-Endpunkt nimmt
 * KEINE Query-Params — gefiltert/sortiert/gezählt wird über die bereits geladenen Zeilen
 * (useMemo). Alle Facetten kombinieren mit UND; leere Felder = inaktiv.
 */
export interface PlatformIqInventoryFilters {
  /** Merchant-Facette (eine der distinkten merchantIds der geladenen Zeilen). */
  merchantId?: string;
  status?: IqServiceInventoryStatus;
  flowType?: IqServiceFlowType;
  bookingType?: IqServiceBookingType;
  /** Freitext über Kennung (iqServiceKey) UND Anzeigename (displayName). */
  text?: string;
}

/** Spec 384 §2: sortierbare Spalten (NICHT die drei Detail-Spalten — v1). */
export type PlatformIqInventorySortKey = 'merchant' | 'status' | 'usage';

export interface PlatformIqInventorySort {
  key: PlatformIqInventorySortKey;
  dir: 'asc' | 'desc';
}

/**
 * Antwort des Aggregat-Endpunkts `GET /admin/iq-service-inventory` (Spec 347 §4).
 * Spiegel des BFF-`OverviewResponse`. `failedMerchants` tragen KEINE Zeilen
 * (nicht als 0-Zeile fehlgelesen); die Degradations-Flags speisen die Banner.
 */
export interface PlatformIqInventoryResponse {
  rows: PlatformIqInventoryRow[];
  /** merchantIds, deren Inventar-Listing geworfen hat — eigene Markierung. */
  failedMerchants: string[];
  /** Globaler Service-Call gescheitert → Zaehler flaechendeckend nicht ermittelbar. */
  usageUnavailable?: boolean;
  /** Globale Seiten-Schranke erreicht → Nutzung ggf. unvollstaendig. */
  usageTruncated?: boolean;
}

/**
 * UI-eigene Listen-Seite. Der Frontend-API-Client mappt die BFF-Antwort
 * (`{ iqServices, total, nextCursor }`) hierauf — das ist die zweite, duenne
 * Mapping-Naht (neben dem BFF-Mapper), die einen etwaigen Feldnamen-Drift
 * der BFF-Seite von der UI fernhaelt. `total` = Anzahl in DIESER Seite (kein
 * Grand-Total); `nextCursor` = null auf der letzten Seite.
 */
export interface IqServiceInventoryListPage {
  items: IqServiceInventoryItem[];
  total: number;
  nextCursor: string | null;
}

/** Serverseitige Filter (AND) fuer die Liste. */
export interface IqServiceInventoryFilters {
  status?: IqServiceInventoryStatus;
  flowType?: IqServiceFlowType;
  bookingType?: IqServiceBookingType;
  /** Default 50, Max 200. */
  limit?: number;
}

/**
 * Create-Body (UC-2 registrieren). `iqServiceKey` opak (Facade validiert das Format);
 * `status` optional (Default `active`).
 */
export interface CreateIqServiceInventoryInput {
  iqServiceKey: string;
  flowType?: IqServiceFlowType;
  bookingType?: IqServiceBookingType;
  displayName?: string;
  description?: string;
  internetqEmailRef?: string;
  status?: IqServiceInventoryStatus;
}

/**
 * Update-Body (UC-3, partiell). `updatedAt` Pflicht (Optimistic Lock). Metadaten-
 * Felder nullable: `null` = entfernen, fehlend = unveraendert.
 */
export interface UpdateIqServiceInventoryInput {
  updatedAt: string;
  flowType?: IqServiceFlowType | null;
  bookingType?: IqServiceBookingType | null;
  displayName?: string | null;
  description?: string | null;
  internetqEmailRef?: string | null;
  status?: IqServiceInventoryStatus;
}
